import { useEffect, useRef, useState } from "react";
import { useRemanso } from "@/context/RemansoContext";
import { liveText } from "@/lib/companion";

const time = (iso) => new Date(iso).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit", hour12: false });

const CareCard = ({ iv }) => (
  <div className="rounded-[24px] bg-riesgo text-tinta p-5" data-testid="care-card">
    <p className="text-[16px] leading-relaxed">{iv.text}</p>
    <p className="text-xs text-tinta mt-3 opacity-80">{time(iv.at)}</p>
  </div>
);

const Intervention = ({ iv, body, onQuote }) => {
  if (iv.kind === "care") return <CareCard iv={iv} />;
  const showQuote = iv.quote && body.includes(iv.quote);
  return (
    <div data-testid={`intervention-${iv.kind}`}>
      {showQuote && (
        <button type="button" onClick={() => onQuote(iv.quote)} data-testid="intervention-quote"
          className="block text-left text-[13px] font-semibold text-acento-texto hover:underline underline-offset-2 mb-1.5">
          “{iv.quote}”
        </button>
      )}
      <p className="text-[18px] leading-snug text-tinta">{iv.text}</p>
      <p className="text-xs text-meta mt-2">{iv.kind === "close" ? `Al cerrar · ${time(iv.at)}` : iv.kind === "giro" ? `Remanso se asomó · ${time(iv.at)}` : time(iv.at)}</p>
    </div>
  );
};

const AFTER_OPTIONS = [["claro", "Más claro"], ["igual", "Igual"], ["revuelto", "Más revuelto"]];

const RevueltoNote = ({ onClose }) => (
  <div className="mt-3">
    <p className="text-[14px] text-sec leading-relaxed" data-testid="after-revuelto-note">
      Escribir a veces mueve cosas antes de acomodarlas. Si te pasa seguido, puede ayudar hablarlo con alguien de confianza o con un profesional.
    </p>
    <button type="button" className="btn-text -ml-2 mt-1" onClick={onClose} data-testid="after-note-ok">Entendido</button>
  </div>
);

// Shown at the end of the thread after "Cerrar la entrada"; collapses to one line once answered
const AfterQuestion = () => {
  const { after, chooseAfter } = useRemanso();
  const [editing, setEditing] = useState(!after);
  const [noteOpen, setNoteOpen] = useState(false);
  const choose = (value) => { chooseAfter(value); setEditing(false); setNoteOpen(value === "revuelto"); };
  const ref = useRef(null);
  useEffect(() => {
    const box = ref.current?.closest("[data-thread-scroll]");
    if (box) box.scrollTop = box.scrollHeight;
  }, [editing, noteOpen]);

  if (after && !editing) {
    const label = AFTER_OPTIONS.find(([v]) => v === after)?.[1];
    return (
      <div ref={ref} key="summary" data-testid="after-question">
        <p className="text-[15px] text-sec" data-testid="after-summary">
          <span>Te dejó: </span><span className="text-tinta font-medium">{label}</span><span> · </span>
          <button type="button" className="text-acento-texto hover:underline underline-offset-2" data-testid="after-change-button"
            onClick={() => { setEditing(true); setNoteOpen(false); }}>Cambiar</button>
        </p>
        {noteOpen && <RevueltoNote onClose={() => setNoteOpen(false)} />}
      </div>
    );
  }
  return (
    <div ref={ref} key="options" data-testid="after-question">
      <p className="text-[18px] leading-snug text-tinta">¿Cómo te deja haber escrito hoy?</p>
      <div className="flex flex-wrap gap-2 mt-3">
        {AFTER_OPTIONS.map(([value, label]) => (
          <button key={value} type="button" aria-pressed={after === value} onClick={() => choose(value)}
            className={`chip ${after === value ? "active" : ""}`} data-testid={`after-${value}`}>{label}</button>
        ))}
      </div>
    </div>
  );
};

const LiveReply = ({ live }) => {
  const text = liveText(live.kind, live.raw);
  return text
    ? <p className="text-[18px] leading-snug text-tinta" data-testid="live-reply">{text}</p>
    : <p className="text-sm text-meta" data-testid="remanso-thinking">Remanso te está leyendo…</p>;
};

export const RemansoControls = ({ prefix, showAsk = true, onAsk }) => {
  const { bound, live, ask, stop } = useRemanso();
  if (!bound) return <p className="text-sm text-sec">Abre una entrada en Escribir para que Remanso te acompañe.</p>;
  if (live) return <button className="btn-secondary self-start" onClick={stop} data-testid={`${prefix}-stop-button`}>Detener</button>;
  return (
    <div className="flex flex-col gap-3">
      {showAsk && (
        <div>
          <button className="btn-primary" onClick={() => (onAsk ? onAsk() : ask("ask"))} data-testid={`${prefix}-ask-button`}
            title="También con Ctrl o Cmd + Enter">Acompáñame</button>
          <p className={`text-[13px] text-meta mt-1.5 ${prefix === "margin" ? "[@media(max-height:760px)]:hidden" : ""}`} data-testid={`${prefix}-ask-hint`}>Te hace una pregunta para seguir escribiendo.</p>
        </div>
      )}
      <div>
        <button className="btn-secondary" onClick={() => ask("close")} data-testid={`${prefix}-close-entry-button`}>Cerrar la entrada</button>
        <p className={`text-[13px] text-meta mt-1.5 ${prefix === "margin" ? "[@media(max-height:760px)]:hidden" : ""}`} data-testid={`${prefix}-close-hint`}>Te devuelve lo que escribiste y una pregunta para llevarte.</p>
      </div>
    </div>
  );
};

export const RemansoThread = ({ prefix }) => {
  const { thread, live, error, draftBody, selectQuote, bound, after } = useRemanso();
  const endRef = useRef(null);
  // keep the newest part of the thread in view inside its own scroll box
  useEffect(() => {
    const box = endRef.current?.closest("[data-thread-scroll]");
    if (box && (thread.length || live)) box.scrollTop = box.scrollHeight;
  }, [thread.length, live, after]);
  if (!bound) return null;
  const lastClose = thread.map((iv) => iv.kind).lastIndexOf("close");
  const items = [];
  thread.forEach((iv, i) => {
    items.push(<Intervention key={iv.id} iv={iv} body={draftBody} onQuote={selectQuote} />);
    if (i === lastClose && !live) items.push(<AfterQuestion key={`after-${iv.id}`} />);
  });
  if (live) items.push(<LiveReply key="live" live={live} />);
  return (
    <div className="flex flex-col" data-testid={`${prefix}-thread`}>
      {items.map((node, i) => (
        <div key={node.key} className={i ? "mt-5 pt-5" : ""}
          style={i ? { borderTopColor: "var(--borde)", borderTopStyle: "dashed", borderTopWidth: 1 } : undefined}>
          {node}
        </div>
      ))}
      {error && <p className="calm-note mt-5" role="status" data-testid={`${prefix}-remanso-error`}>{error}</p>}
      <div ref={endRef} />
    </div>
  );
};
